import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import {
  faPercent,
  faSortNumericUp,
  faSquareMinus,
  faSquarePlus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import Radio from "antd/es/radio";
import Select from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import Highcharts, { SeriesClickEventObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useIntl } from "react-intl";

import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Team from "@mapmycustomers/shared/types/Team";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { AvatarWithName, Button } from "@mapmycustomers/ui";

import DataViewDisplayType from "@app/enum/DataViewDisplayType";
import DataViewMode, { getDataModeDisplayName } from "@app/enum/DataViewMode";
import { RootState } from "@app/store/rootReducer";
import allColors from "@app/styles/_colors.module.scss";

import messages from "../../messages";
import { selectDataDisplayType, selectDataMode } from "../../store/actions";
import {
  getDataDisplayType,
  getDataMode,
  getField,
  getTotalValue,
  getUserData,
} from "../../store/selectors";
import { UserDataRecord } from "../../type/DataRecord";
import {
  COLUMN_BAR_HEIGHT,
  NO_TEAM_ID,
  PIE_VIEW_LIMIT,
  TEAM_COUNT_TO_SHOW,
} from "../../util/const";
import getRandomColor from "../../util/getRandomColor";
import sortByValue from "../../util/sortByValue";
import useChartConfig from "../../util/useChartConfig";
import useGrossValueFormatter from "../../util/useGrossValueFormatter";
import usePercentageValueFormatter from "../../util/usePercentageValueFormatter";
import useTeamChartData from "../../util/useTeamChartData";

import styles from "./Breakdown.module.scss";

interface Props {
  dataDisplayType: DataViewDisplayType;
  dataMode: DataViewMode;
  field?: IField;
  selectDataDisplayType: typeof selectDataDisplayType;
  selectDataMode: typeof selectDataMode;
  totalValue: number;
  userData: UserDataRecord[];
}

const Breakdown: React.FC<Props> = ({
  dataDisplayType,
  dataMode,
  field,
  selectDataDisplayType,
  selectDataMode,
  totalValue,
  userData,
}) => {
  const intl = useIntl();
  const [selectedTeams, setSelectedTeams] = useState<Map<Team["id"], Team["name"]>>(new Map());
  const [showAll, , , toggleShowAll] = useBoolean();

  const selectedTeamIds = useMemo(() => new Set(selectedTeams.keys()), [selectedTeams]);

  const handleBarClick = useCallback(
    (event: SeriesClickEventObject) => {
      const { id, name } = event.point.options.custom?.team as Team;
      const result = new Map(selectedTeams);
      if (result.has(id)) {
        result.delete(id);
      } else {
        result.set(id, name);
      }
      setSelectedTeams(result);
    },
    [selectedTeams, setSelectedTeams]
  );

  const singleTeamName: string | undefined = useMemo(() => {
    let teamName = undefined;
    if (selectedTeams.size === 1) {
      const [key, value] = [...selectedTeams][0];
      if (key === NO_TEAM_ID) {
        teamName = intl.formatMessage(messages.noTeam);
      } else {
        teamName = value;
      }
    }
    return teamName;
  }, [intl, selectedTeams]);

  const teamChartData = useTeamChartData(intl, userData, selectedTeamIds, dataMode);

  const colors = useMemo(
    () =>
      teamChartData.map(
        ({
          custom: {
            team: { id },
          },
        }) =>
          selectedTeams.size === 0 || selectedTeams.has(id)
            ? getRandomColor(id)
            : allColors.disabledGray
      ),
    [selectedTeams, teamChartData]
  );

  const grossValueFormatter = useGrossValueFormatter(intl, field);
  const percentageValueFormatter = usePercentageValueFormatter(intl);
  const displayTypeDependedValueFormatter =
    dataDisplayType === DataViewDisplayType.GROSS ? grossValueFormatter : percentageValueFormatter;
  const usePie = teamChartData.length <= PIE_VIEW_LIMIT && dataMode === DataViewMode.SUM;

  const chartConfig = useChartConfig({
    colors,
    columnChartHeight: COLUMN_BAR_HEIGHT * teamChartData.length,
    dataDisplayType,
    dataViewMode: dataMode,
    grossValueFormatter,
    intl,
    onBarClick: handleBarClick,
    percentageValueFormatter,
    seriesData: teamChartData,
    totalValue,
    usePie,
  });

  const handleChangeMode = useCallback(
    (event) => {
      selectDataDisplayType(event.target.value);
    },
    [selectDataDisplayType]
  );

  const [filteredUserData, total] = useMemo(() => {
    let filteredUserData: UserDataRecord[] = [];
    let total = 0;
    const teamIds = new Set(selectedTeams.keys());

    userData.forEach((record) => {
      if (
        !teamIds.size ||
        record.entity.teams.some(({ id }) => teamIds.has(id)) ||
        (teamIds.has(NO_TEAM_ID) && record.entity.teams.length === 0)
      ) {
        filteredUserData.push(record);
        total += record.value;
      }
    });
    if (dataDisplayType === DataViewDisplayType.PERCENTAGE && total) {
      filteredUserData = filteredUserData.map((record) => ({
        ...record,
        value: record.value / total,
      }));
    }
    if (dataMode === DataViewMode.AVG && filteredUserData.length > 0) {
      total = total / filteredUserData.length;
    }
    return [sortByValue(filteredUserData), total] as [UserDataRecord[], number];
  }, [dataDisplayType, dataMode, selectedTeams, userData]);

  return (
    <div>
      <div className={styles.totalValue}>{grossValueFormatter(totalValue)}</div>
      <Select<DataViewMode>
        className={styles.dataMode}
        dropdownMatchSelectWidth={false}
        onChange={selectDataMode}
        value={dataMode}
      >
        <Select.Option value={DataViewMode.SUM}>
          {getDataModeDisplayName(intl, DataViewMode.SUM)}
        </Select.Option>
        <Select.Option value={DataViewMode.AVG}>
          {getDataModeDisplayName(intl, DataViewMode.AVG)}
        </Select.Option>
      </Select>
      <Row align="middle" className={styles.infoBlock} justify="space-between">
        <Col>{intl.formatMessage(messages.teamBreakdown)}</Col>
        <Col>
          <Radio.Group
            buttonStyle="solid"
            className={styles.modeChanger}
            onChange={handleChangeMode}
            value={dataDisplayType}
          >
            <Tooltip title={intl.formatMessage(messages.viewPercent)}>
              <Radio.Button value={DataViewDisplayType.PERCENTAGE}>
                <FontAwesomeIcon icon={faPercent} />
              </Radio.Button>
            </Tooltip>
            <Tooltip title={intl.formatMessage(messages.viewGross)}>
              <Radio.Button value={DataViewDisplayType.GROSS}>
                <FontAwesomeIcon icon={faSortNumericUp} />
              </Radio.Button>
            </Tooltip>
          </Radio.Group>
        </Col>
      </Row>
      <div
        className={styles.chartContainer}
        style={{ maxHeight: showAll ? "100%" : COLUMN_BAR_HEIGHT * TEAM_COUNT_TO_SHOW }}
      >
        <HighchartsReact highcharts={Highcharts} isPureConfig options={chartConfig} />
      </div>
      {teamChartData.length > TEAM_COUNT_TO_SHOW && (
        <Row justify="center">
          <Col>
            <Button
              icon={
                showAll ? (
                  <FontAwesomeIcon icon={faSquareMinus} />
                ) : (
                  <FontAwesomeIcon icon={faSquarePlus} />
                )
              }
              iconOnly
              onClick={toggleShowAll}
              type="link"
            >
              {showAll
                ? intl.formatMessage(messages.showLess)
                : intl.formatMessage(messages.showMore)}
            </Button>
          </Col>
        </Row>
      )}
      <Row align="middle" className={styles.infoBlock} justify="space-between">
        <Col>
          {singleTeamName ??
            (selectedTeams.size && selectedTeams.size < teamChartData.length
              ? intl.formatMessage(messages.multipleTeams)
              : intl.formatMessage(messages.allUsers))}
        </Col>
        <Col>{grossValueFormatter(total)}</Col>
      </Row>
      {filteredUserData.map(({ entity, value }) => (
        <Row className={styles.userData} justify="space-between" key={entity.id}>
          <Col className={styles.userName} span={16}>
            <AvatarWithName user={entity} />
          </Col>
          <Col span={7}>
            <Row justify="end">
              <Col>{displayTypeDependedValueFormatter(value)}</Col>
            </Row>
          </Col>
        </Row>
      ))}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataDisplayType: getDataDisplayType(state),
  dataMode: getDataMode(state),
  field: getField(state),
  totalValue: getTotalValue(state),
  userData: getUserData(state),
});

const mapDispatchToProps = {
  selectDataDisplayType,
  selectDataMode,
};

export default connect(mapStateToProps, mapDispatchToProps)(Breakdown);
